import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Search, Lightbulb } from 'lucide-react';
import { API, INSPIRATION_CATEGORIES, PAGE_SIZE } from './constants';
import { ConfirmDialog } from './ConfirmDialog';

export const InspirationPanel = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [form, setForm] = useState({ content: '', category: 'post', tags: '' });

  const fetchPage = useCallback(async (skip) => {
    const params = new URLSearchParams();
    if (category !== 'all') params.set('category', category);
    if (query) params.set('q', query);
    params.set('skip', String(skip));
    params.set('limit', String(PAGE_SIZE));
    const res = await axios.get(`${API}/content/inspirations?${params.toString()}`, getAuthHeaders());
    return res.data;
  }, [getAuthHeaders, category, query]);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchPage(0);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch (e) { console.error(e); }
  }, [fetchPage]);

  useEffect(() => {
    const t = setTimeout(refresh, 200);
    return () => clearTimeout(t);
  }, [refresh]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(items.length);
      setItems(prev => [...prev, ...(data.items || [])]);
      setHasMore(!!data.has_more);
    } catch (e) { toast.error('Failed to load more'); }
    finally { setLoadingMore(false); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ content: '', category: 'post', tags: '' });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ content: item.content, category: item.category, tags: item.tags || '' });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.content.trim()) return toast.error('Content required');
    try {
      if (editing) {
        await axios.put(`${API}/content/inspirations/${editing.inspiration_id}`, form, getAuthHeaders());
      } else {
        await axios.post(`${API}/content/inspirations`, form, getAuthHeaders());
      }
      toast.success('Saved');
      setDialogOpen(false);
      refresh();
    } catch (e) { toast.error('Failed'); }
  };
  const remove = async () => {
    if (!pendingDeleteId) return;
    try {
      await axios.delete(`${API}/content/inspirations/${pendingDeleteId}`, getAuthHeaders());
      toast.success('Deleted');
      refresh();
    } catch (e) {
      toast.error('Failed');
    } finally {
      setPendingDeleteId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search ideas or tags..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="inspiration-search"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-36" data-testid="inspiration-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {INSPIRATION_CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button size="sm" className="w-full bg-sky-600 hover:bg-sky-700" onClick={openNew} data-testid="inspiration-add-btn">
        <Plus className="w-4 h-4 mr-1" /> Save New Idea
      </Button>

      {items.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <Lightbulb className="w-8 h-8 mx-auto mb-2 text-amber-400" />
            <p className="text-sm text-muted-foreground">
              {query || category !== 'all' ? 'No ideas match your filters.' : 'Save your first content idea to build your inspiration library.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-xs text-muted-foreground text-right" data-testid="inspiration-count">
            Showing {items.length} of {total}
          </p>
          <div className="space-y-2">
            {items.map(it => (
              <Card key={it.inspiration_id} className="border-sky-100 dark:border-sky-900">
                <CardContent className="p-3">
                  <div className="flex justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex gap-1.5 flex-wrap mb-1">
                        <Badge variant="secondary" className="capitalize text-[10px]">{it.category}</Badge>
                        {it.tags && it.tags.split(',').map((t, i) => t.trim() && (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-800">
                            #{t.trim()}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm whitespace-pre-wrap break-words">{it.content}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(it)} data-testid={`inspiration-edit-${it.inspiration_id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => setPendingDeleteId(it.inspiration_id)} data-testid={`inspiration-delete-${it.inspiration_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {hasMore && (
            <Button
              variant="outline"
              className="w-full border-sky-300 text-sky-600"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="inspiration-load-more"
            >
              {loadingMore ? 'Loading...' : `Load ${Math.min(PAGE_SIZE, total - items.length)} more`}
            </Button>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Idea' : 'Save Idea'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="inspiration-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INSPIRATION_CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea
                value={form.content} rows={5}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Your idea, hook, quote, or note..."
                data-testid="inspiration-content-input"
              />
            </div>
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="e.g. mindset, morning, reels"
                data-testid="inspiration-tags-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="inspiration-save-btn">
              {editing ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDeleteId}
        onOpenChange={(o) => !o && setPendingDeleteId(null)}
        onConfirm={remove}
        title="Delete this idea?"
        description="This inspiration will be permanently removed from your library."
        testId="inspiration-confirm-delete"
      />
    </div>
  );
};

export default InspirationPanel;
